import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import { Badge } from '@/common/ui/badge'
import { CheckCircle2 } from 'lucide-react'
import { cn } from '@/common/utils'

export interface PresetOption {
	preset: string
	name: string
	description: string
	defaultMaxItems: number
	requiredFields: string[]
	optionalFields: string[]
}

interface BrokeFlexPresetSelectorProps {
	presets: PresetOption[]
	selectedPreset: string | null
	onSelectPreset: (preset: string) => void
	isLoading?: boolean
}

const PRESET_ICONS: Record<string, string> = {
	SIMPLE_PROMOTION: '🎁',
	THREE_PROMOTIONS: '🎯',
	PROMOTION_WITH_BONUSES: '💎',
	COUPON_BLAST: '🎫',
	ALL_IN_RECOVERY: '🚀',
}

export default function BrokeFlexPresetSelector({
	presets,
	selectedPreset,
	onSelectPreset,
	isLoading,
}: BrokeFlexPresetSelectorProps) {
	if (isLoading) {
		return (
			<Card>
				<CardHeader>
					<CardTitle className="text-2xl">เลือกรูปแบบแคมเปญ</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
						{[1, 2, 3, 4, 5, 6].map((i) => (
							<div key={i} className="h-48 animate-pulse rounded-lg bg-muted" />
						))}
					</div>
				</CardContent>
			</Card>
		)
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle className="text-2xl">เลือกรูปแบบแคมเปญ</CardTitle>
				<p className="text-base text-muted-foreground">
					เลือกรูปแบบที่เหมาะกับการใช้งานของคุณ
					คุณสามารถปรับแต่งเพิ่มเติมได้ในภายหลัง
				</p>
			</CardHeader>
			<CardContent>
				<div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
					{presets.map((preset, index) => {
						const isSelected = selectedPreset === preset.preset
						const isRecommended = index === 0

						return (
							<button
								key={preset.preset}
								type="button"
								onClick={() => onSelectPreset(preset.preset)}
								className={cn(
									'group relative flex flex-col gap-4 rounded-lg border-2 p-6 text-left transition-all hover:border-primary hover:shadow-lg',
									isSelected
										? 'border-primary bg-primary/5 shadow-md'
										: 'border-border bg-background',
								)}
							>
								<div className="absolute right-3 top-3 flex items-center gap-2">
									{isRecommended && (
										<Badge className="bg-green-600 text-sm hover:bg-green-700">
											แนะนำ
										</Badge>
									)}
									{isSelected && (
										<CheckCircle2 className="h-6 w-6 text-primary" />
									)}
								</div>

								<div className="flex items-start gap-4 pt-2">
									<div className="text-5xl">
										{PRESET_ICONS[preset.preset] || '📋'}
									</div>
									<div className="flex-1">
										<h3 className="mb-3 text-xl font-semibold">
											{preset.name}
										</h3>
										<p className="text-base leading-relaxed text-muted-foreground">
											{preset.description}
										</p>
									</div>
								</div>

								<div className="flex flex-wrap items-center gap-2">
									<Badge variant="outline" className="px-3 py-1 text-sm">
										สูงสุด {preset.defaultMaxItems} รายการ
									</Badge>
									{preset.requiredFields.includes('promotionIds') && (
										<Badge variant="secondary" className="px-3 py-1 text-sm">
											โปรโมชั่น
										</Badge>
									)}
									{preset.optionalFields.includes('includeReturnLoss') && (
										<Badge variant="secondary" className="px-3 py-1 text-sm">
											โบนัส
										</Badge>
									)}
								</div>
							</button>
						)
					})}

					{/* Custom/Advanced Card */}
					<button
						type="button"
						onClick={() => onSelectPreset('CUSTOM')}
						className={cn(
							'group relative flex flex-col gap-4 rounded-lg border-2 border-dashed p-6 text-left transition-all hover:border-primary hover:shadow-lg',
							selectedPreset === 'CUSTOM'
								? 'border-primary bg-primary/5 shadow-md'
								: 'border-border bg-background',
						)}
					>
						{selectedPreset === 'CUSTOM' && (
							<div className="absolute right-3 top-3">
								<CheckCircle2 className="h-6 w-6 text-primary" />
							</div>
						)}

						<div className="flex items-start gap-4">
							<div className="text-5xl">⚙️</div>
							<div className="flex-1">
								<h3 className="mb-2 text-xl font-semibold">
									กำหนดเอง (ขั้นสูง)
								</h3>
								<p className="text-base leading-relaxed text-muted-foreground">
									สร้างแคมเปญแบบกำหนดเองทุกอย่างด้วยตัวเอง
									เหมาะสำหรับผู้ที่ต้องการควบคุมแบบละเอียด
								</p>
							</div>
						</div>

						<div className="flex flex-wrap items-center gap-2">
							<Badge variant="outline" className="px-3 py-1 text-sm">
								ไม่จำกัด
							</Badge>
							<Badge variant="secondary" className="px-3 py-1 text-sm">
								ขั้นสูง
							</Badge>
						</div>
					</button>
				</div>
			</CardContent>
		</Card>
	)
}
